import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  Link,
  useNavigate
} from 'react-router-dom';

import './AdminPartners.scss';

export default function AdminPartners() {
  const navigate =
    useNavigate();

  const apiBaseUrl =
    useMemo(() => {
      const envUrl =
        process.env
          .REACT_APP_API_URL ||
        '';

      return envUrl.endsWith('/')
        ? envUrl.slice(
            0,
            -1
          )
        : envUrl;
    }, []);

  const token =
    localStorage.getItem(
      'tg_admin_token'
    );

  const [
    partners,
    setPartners
  ] = useState([]);

  const [
    search,
    setSearch
  ] = useState('');

  const [
    typeFilter,
    setTypeFilter
  ] = useState('ALL');

  const [
    statusFilter,
    setStatusFilter
  ] = useState('ALL');

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    error,
    setError
  ] = useState('');

  useEffect(() => {
    const fetchPartners =
      async () => {
        setLoading(true);
        setError('');

        try {
          const response =
            await fetch(
              `${apiBaseUrl}/admin/partners`,
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`
                }
              }
            );

          const data =
            await response.json();

          if (!response.ok) {
            throw new Error(
              data?.message ||
                'Unable to load partners.'
            );
          }

          setPartners(
            data.partners || []
          );
        } catch (err) {
          setError(
            err.message ||
              'Unable to load partners.'
          );
        } finally {
          setLoading(false);
        }
      };

    fetchPartners();
  }, [
    apiBaseUrl,
    token
  ]);

  const types =
    useMemo(() => {
      return [
        ...new Set(
          partners.map(
            (partner) =>
              partner.partner_type
          )
        )
      ].filter(Boolean);
    }, [partners]);

  const statuses =
    useMemo(() => {
      return [
        ...new Set(
          partners.map(
            (partner) =>
              partner.status
          )
        )
      ].filter(Boolean);
    }, [partners]);

  const filteredPartners =
    useMemo(() => {
      const normalized =
        search
          .trim()
          .toLowerCase();

      return partners.filter(
        (partner) => {
          const matchesSearch =
            !normalized ||
            partner.organization_name
              ?.toLowerCase()
              .includes(
                normalized
              ) ||
            partner.contact_name
              ?.toLowerCase()
              .includes(
                normalized
              ) ||
            partner.contact_email
              ?.toLowerCase()
              .includes(
                normalized
              ) ||
            partner.course_name
              ?.toLowerCase()
              .includes(
                normalized
              );

          const matchesType =
            typeFilter ===
              'ALL' ||
            partner.partner_type ===
              typeFilter;

          const matchesStatus =
            statusFilter ===
              'ALL' ||
            partner.status ===
              statusFilter;

          return (
            matchesSearch &&
            matchesType &&
            matchesStatus
          );
        }
      );
    }, [
      partners,
      search,
      typeFilter,
      statusFilter
    ]);

  const formatDate = (
    value
  ) => {
    if (!value) {
      return '—';
    }

    return new Date(
      value
    ).toLocaleDateString(
      undefined,
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }
    );
  };

  const dueFollowUps =
    partners.filter(
      (partner) => {
        if (
          !partner.next_follow_up_at
        ) {
          return false;
        }

        return (
          new Date(
            partner.next_follow_up_at
          ) <= new Date()
        );
      }
    ).length;

  return (
    <div className="admin-partners-page">

      <section className="admin-partners-heading">

        <div>
          <p className="admin-partners-heading__eyebrow">
            Relationship Management
          </p>

          <h2>
            Partners
          </h2>

          <p>
            Manage course,
            simulator, venue and
            brand relationships.
          </p>
        </div>

        <Link
          to="/admin/partners/new"
          className="admin-partners-heading__button"
        >
          + Add Partner
        </Link>

      </section>

      <section className="admin-partner-summary">

        <div>
          <span>
            Total
          </span>

          <strong>
            {partners.length}
          </strong>
        </div>

        <div>
          <span>
            Prospects
          </span>

          <strong>
            {
              partners.filter(
                (partner) =>
                  partner.status ===
                  'PROSPECT'
              ).length
            }
          </strong>
        </div>

        <div>
          <span>
            Active Partners
          </span>

          <strong>
            {
              partners.filter(
                (partner) =>
                  partner.status ===
                  'PARTNER'
              ).length
            }
          </strong>
        </div>

        <div>
          <span>
            Follow-Ups Due
          </span>

          <strong>
            {dueFollowUps}
          </strong>
        </div>

      </section>

      <section className="admin-partner-filters">

        <div className="admin-partner-filters__search">
          <label>
            Search
          </label>

          <input
            value={
              search
            }
            onChange={(event) =>
              setSearch(
                event.target.value
              )
            }
            placeholder="Organization, contact or course..."
          />
        </div>

        <div>
          <label>
            Type
          </label>

          <select
            value={
              typeFilter
            }
            onChange={(event) =>
              setTypeFilter(
                event.target.value
              )
            }
          >
            <option value="ALL">
              All Types
            </option>

            {types.map(
              (type) => (
                <option
                  key={type}
                  value={type}
                >
                  {type}
                </option>
              )
            )}
          </select>
        </div>

        <div>
          <label>
            Status
          </label>

          <select
            value={
              statusFilter
            }
            onChange={(event) =>
              setStatusFilter(
                event.target.value
              )
            }
          >
            <option value="ALL">
              All Statuses
            </option>

            {statuses.map(
              (status) => (
                <option
                  key={status}
                  value={status}
                >
                  {status}
                </option>
              )
            )}
          </select>
        </div>

      </section>

      {error ? (
        <div className="admin-partners-error">
          {error}
        </div>
      ) : null}

      <section className="admin-partners-table-card">

        <div className="admin-partners-table-card__header">
          <div>
            <h3>
              Relationship Pipeline
            </h3>

            <span>
              {filteredPartners.length}
              {' '}
              records
            </span>
          </div>
        </div>

        {loading ? (
          <div className="admin-partners-empty">
            Loading partners...
          </div>
        ) : filteredPartners.length ===
          0 ? (
          <div className="admin-partners-empty">
            No partners found.
          </div>
        ) : (
          <div className="admin-partners-table-wrapper">

            <table className="admin-partners-table">

              <thead>
                <tr>
                  <th>
                    Organization
                  </th>

                  <th>
                    Type
                  </th>

                  <th>
                    Status
                  </th>

                  <th>
                    Contact
                  </th>

                  <th>
                    Last Contact
                  </th>

                  <th>
                    Next Follow-Up
                  </th>

                  <th>
                    Course
                  </th>

                  <th>
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredPartners.map(
                  (partner) => (
                    <tr
                      key={
                        partner.partner_id
                      }
                    >
                      <td>
                        <strong>
                          {
                            partner.organization_name
                          }
                        </strong>
                      </td>

                      <td>
                        {
                          partner.partner_type
                        }
                      </td>

                      <td>
                        <span className="admin-partner-status">
                          {
                            partner.status
                          }
                        </span>
                      </td>

                      <td>
                        <div className="admin-partner-contact">
                          <strong>
                            {partner.contact_name ||
                              '—'}
                          </strong>

                          <span>
                            {partner.contact_email ||
                              ''}
                          </span>
                        </div>
                      </td>

                      <td>
                        {formatDate(
                          partner.last_contact_at
                        )}
                      </td>

                      <td>
                        {formatDate(
                          partner.next_follow_up_at
                        )}
                      </td>

                      <td>
                        {partner.course_name ||
                          '—'}
                      </td>

                      <td>
                        <button
                          type="button"
                          className="admin-partner-manage"
                          onClick={() =>
                            navigate(
                              `/admin/partners/${partner.partner_id}`
                            )
                          }
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  )
                )}
              </tbody>

            </table>

          </div>
        )}

      </section>

    </div>
  );
}